import { datadogAddAction } from "./datadogHelper";

/**
 * Wraps a Firebase Auth call with retry logic for transient network failures.
 *
 * Retries the function up to 3 times with exponential backoff (250 / 500 / 1000 ms)
 * but ONLY when the error code is `auth/network-request-failed`. Any other error
 * is re-thrown immediately so callers can handle them (e.g. `auth/invalid-custom-token`).
 *
 * On a successful retry, emits a Datadog action so we can observe how often
 * the retry actually saved a failed auth call in production.
 *
 * Designed for `signInAnonymously`, `signInWithEmailAndPassword`, and
 * `signInWithCustomToken` which are vulnerable to transient fetch failures
 * in iOS WebKit and in-app WebViews (Facebook, Instagram, Snapchat).
 */
export async function retryOnNetworkFail<T>(fn: () => Promise<T>): Promise<T> {
  const BACKOFF_MS = [250, 500, 1000];
  let lastError: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const result = await fn();
      if (attempt > 0) {
        datadogAddAction("Firebase auth: succeeded after retry", { attempt });
      }
      return result;
    } catch (e) {
      lastError = e;
      const code = (e as { code?: string })?.code;
      if (code !== "auth/network-request-failed") throw e;
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, BACKOFF_MS[attempt]));
      }
    }
  }
  datadogAddAction("Firebase auth: all retries exhausted", { attempts: 3 });
  throw lastError;
}
